/**
 * @author lifei
 * 功能： 处理页签，删除和新增等操作与浏览器逻辑基本一致
 */
import { isNull, isObject, isString, omit } from 'lodash-es'
import { Cache, URL } from '@/tools'
import * as ENUM from '@/enum'

export class Tab<T extends TCommon.TStore.State> {
  /**
   * 功能：获取存储的 tabs 数据
   */
  getList = (rootState: T): Promise<T['sTabs']> => {
    const sTabs = Cache.TABS.getItem<T['sTabs']>() || []
    rootState.sTabs = sTabs
    return Promise.resolve(sTabs)
  }

  /**
   * 功能：存储页面标签 tab，在 query 中使用 t 自定义 tabbar 的标题
   */
  add = (rootState: T, state: T['sTab']): Promise<T['sTabs']> => {
    const { sFavorites, sTabs } = rootState

    if (isObject(state)) {
      const { fullPath, meta, path, query } = state

      // 使用全路径处理逻辑
      const tab = sFavorites.find((item) => item.fullPath === fullPath)
      if (tab?.meta && meta) {
        meta.collected = tab.meta.collected
      }

      const activeTab = sTabs.find((item) => item.fullPath === fullPath)

      if (activeTab) {
        activeTab.meta = {
          ...activeTab.meta,
          ...meta, // 更新 meta
        }
        state = activeTab
      } else {
        // 1. 是否需要添新的页签, path 不存在则添加新的页签, 存在则改变改变其值
        // 2. 如果 path 相同则直接整体替换成最新的 route 对象
        const activeIndex = sTabs.findIndex((item) => item.path === path)
        if (activeIndex > -1) {
          sTabs[activeIndex] = state
        } else {
          rootState.sTabs.push(state)
        }
      }

      if (isString(query.t) && state.meta) {
        state.meta.title = URL.decodeURIComponent(query.t)
      }

      this.active(rootState, state)
    }

    return new Promise((resolve) => {
      Cache.TABS.setItem(rootState.sTabs)
      resolve(rootState.sTabs)
    })
  }

  /**
   * 功能：获取当前的 tab
   */
  getActive = (rootState: T): Promise<T['sActiveTab']> => {
    const sActiveTab = Cache.ACTIVE_TAB.getItem() || rootState.sTabs[0]
    this.active(rootState, sActiveTab)
    rootState.sActiveTab = sActiveTab
    return Promise.resolve(sActiveTab)
  }

  /**
   * 功能： 当前活跃的 tab，有性能问题，后续优化
   */
  active = (rootState: T, state: T['sActiveTab']): Promise<T['sActiveTab']> => {
    rootState.sActiveTab = state
    return new Promise((resolve) => {
      Cache.ACTIVE_TAB.setItem(state)
      resolve(state)
    })
  }

  /**
   * 功能：删除 tab，仿浏览器规则，后续考虑是否根据路由关系，激活上一级路由页面。
   */
  remove = (rootState: T, activeKey: string) => {
    const { sTabs, sActiveTab } = rootState
    const index = sTabs.findIndex((item) => item.fullPath === activeKey)
    if (sActiveTab?.fullPath === activeKey) {
      const nextTab = sTabs[index + 1]
      if (nextTab) {
        this.active(rootState, nextTab)
      } else {
        const prevTab = sTabs[index - 1]
        this.active(rootState, prevTab)
      }
    }

    sTabs.splice(index, 1)
    rootState.sTabs = sTabs

    return new Promise((resolve) => {
      Cache.TABS.setItem(rootState.sTabs)
      resolve(rootState.sTabs)
    })
  }

  /**
   * 功能：移除多个 tab, 批操作处理
   */
  removeMore = (
    rootState: T,
    state: {
      mode: Exclude<TCommon.EnumVal<typeof ENUM.COMMON.MapTabOperate>, 'refresh'>
      clickTab: T['sTabs'][number]
    }
  ) => {
    const { mode, clickTab } = state
    const { sActiveTab, sTabs } = rootState
    const index = sTabs.findIndex((item) => item.fullPath === clickTab.fullPath)
    const maxIndex = sTabs.length - 1

    if (mode == 'left') {
      rootState.sTabs = sTabs.slice(index, maxIndex)
    } else if (mode === 'right') {
      rootState.sTabs = sTabs.slice(0, index + 1)
    } else if (mode === 'self') {
      this.remove(rootState, clickTab.fullPath)
    } else if (mode === 'other') {
      if (sActiveTab?.fullPath === clickTab.fullPath) {
        if (!isNull(sActiveTab)) {
          rootState.sTabs = [sActiveTab]
        }
      } else {
        rootState.sTabs = [clickTab]
        this.active(rootState, clickTab)
      }
    } else {
      const _exhaustiveCheck: never = mode
    }

    return new Promise((resolve) => {
      Cache.TABS.setItem(rootState.sTabs)
      resolve(rootState.sTabs)
    })
  }

  /**
   * 功能：更新页签列表
   */
  updateList = (rootState: T, state: Exclude<T['sActiveTab'], null>): Promise<T['sTabs']> => {
    const { sTabs } = rootState
    return new Promise((resolve) => {
      Cache.TABS.setItem(sTabs)
      resolve(sTabs)
    })
  }

  /**
   * 功能：清空页签
   */
  empty = (rootState: T): Promise<void> => {
    rootState.sTabs = []
    Cache.TABS.removeItem()
    return Promise.resolve()
  }
}
